<template>
	<v-card class="forum-preview-card elevation-0 d-flex flex-column pt-2 mb-1">
		<div class="forum-preview-card__container">
			<div class="forum-preview-card__outer">
				<div class="d-flex forum-preview-card__list pl-0 pr-0 pt-5">

					<router-link
						:to="{
							name: isCurrentUser(this.forum.user.user_id) ? 'profile' : 'user-by-id',
							params: isCurrentUser(this.forum.user.user_id) ? [] : {id: this.forum.user.user_id}
						}"
						class="text-decoration-none mb-1"
						:class="{ disabled: this.forum.user.user_hidden }"
					>
						<div class="forum-preview-card__header px-6">
							<div class="d-flex align-center">
								<Avatar class="mr-2" :size="50" :avatar="forum.user.user_logo" />
								<div
									class="d-flex flex-column justify-center gap-4 forum-preview-card__meta"
								>
									{{ forum.user.visible_username }}
									<span class="forum-preview-card__meta-bottom">{{ forum.user.second_profile_row }}</span>
								</div>
							</div>
							<div class="forum-preview-card__meta-time">{{ calcDateAgo(forum.created) }}</div>
						</div>
					</router-link>
					<v-divider class="mt-2" />
					
					<router-link 
						:to="{ name: 'forum-by-id', params: { id: forum.forum_id } }" 
						style="text-decoration: none;"
					>
						<v-card-title class="forum-preview-card__title pb-0 pt-5 px-4" v-html="formattedText" />
					</router-link>

					<div v-if="forum.uploads && forum.uploads.length" class="mt-5">
						<div
							class="d-flex flex-column flex-wrap w-full px-6 pb-4 mb-n2"
							v-for="attachment in this.forum.uploads"
							:key="attachment.upload_id"
						>
							<SessionAttachments :attachment="attachment" />
						</div>
					</div>

					<v-divider class="mt-5" />

					<div class="d-flex justify-space-between align-center px-6 py-4">
						<div class="d-flex">
							<div class="d-flex align-center mr-6">
								<Icon 
									type="eye"
									color="var(--accent-color)"
									:size="22"
									:pr="8"
								/>
								<div class="forum-preview-card__counter-num">
									{{ forum.views_count }}
								</div>
							</div>
							<div class="d-flex align-center">
								<Icon 
									type="comment"
									color="var(--accent-color)"
									:size="22"
									:pr="8"
								/>
								<div class="forum-preview-card__counter-num">
									{{ forum.comment_count }}
								</div>
							</div>
						</div>
						<div class="forum-preview-card__reply">
							<router-link :to="{
								name: 'forum-by-id',
								params: { id: forum.forum_id },
								query: {toBottom: true}
							}">
								Reply
							</router-link>
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import {mapGetters} from 'vuex'
import Avatar from '@/components/Avatar'
import moment from 'moment'
import SessionAttachments from '@/components/Resource/SessionAttachments.vue'

Vue.use(VueScrollTo)

export default {
	name: 'ForumPreview',
	components: {
		Avatar,
		SessionAttachments

	},
	props: {
		forum: {
			type: Object,
			default: () => {},
		},
	},
	methods: {
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const {user_id: currentUserId} = this.profile
			return userId === currentUserId;
		},
		calcDateAgo(date) {
			return moment(date * 1000).fromNow()
		}
	},
	computed: {
		...mapGetters('auth', ['isAuthenticated']),
		...mapGetters('auth', ['profile', 'isAuthenticated']),

		formattedText() {
			const parser = new DOMParser()
			const doc = parser.parseFromString(this.forum.forum_text, 'text/html')
			return doc.body.innerHTML
		}
	},

}
</script>

<style lang="scss">
.forum-preview-card {
	background: #f1f1f1 !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.06);
		background: white !important;
	}

	&__content-inner {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		line-height: 18px;
		padding-top: 10px;

		img {
			width: 100%;
		}

		p {
			margin-bottom: 0;
		}

		h1 {
			font-size: 30px;
			padding-bottom: 8px;
			line-height: 30px;
		}
	}

	&__counter {
		&-num {
			color: #697583;
			font-size: 15px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-align: right;
		}
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;

		@media (max-width: 480px) {
			flex-direction: column;
		}
	}

	&__reply {
		display: flex;
		justify-content: flex-end;
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-decoration: underline;
		cursor: pointer;

		a {
			color: var(--accent-color) !important;
			margin-left: 4px;
		}
	}

	&__meta {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: var(--username-category-title);
		letter-spacing: 0.36px;
		gap: 4px;
		word-break: break-word;

		&-bottom {
			color: var(--company-position-large);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			line-height: 16px;
		}

		&-time {
			color: rgb(155, 155, 155);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			text-align: right;

			@media (max-width: 480px) {
				text-align: left;
				padding-left: 58px;
			}
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__link {
		color: rgb(66, 160, 216);
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-decoration: underline;
		cursor: pointer;

		a {
			color: rgb(66, 160, 216) !important;
		}
	}
	&__title {
		color: #4a4a4a;
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 400;
		line-height: 17.07px!important;
		word-break: break-word;
		white-space: pre-wrap;
	}

	&__content {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		height: auto;
		width: 100%;
	}

	&__menu-dots {
		margin-left: 10px !important;
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
}

</style>
